import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import { supabase } from '../lib/supabase'
import { formatDate } from '../lib/utils'
import type { LogAuditoria } from '../types/database'

export function AuditoriaPage() {
  const { profile } = useAuth()
  const [logs, setLogs] = useState<(LogAuditoria & { usuario?: { nome: string } })[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  type LogRow = LogAuditoria & { usuario?: { nome: string } }
  const tableSort = useTableSort<LogRow>('data', 'desc')

  const allowed = profile?.perfil === 'admin' || profile?.perfil === 'fiscal'

  const load = useCallback(async () => {
    if (!allowed) return
    setLoading(true)
    const { data, error: err } = await supabase
      .from('log_auditoria')
      .select('*, usuario:usuarios(nome)')
      .order('criado_em', { ascending: false })
      .limit(300)
    if (err) setError(err.message)
    else setLogs((data as typeof logs) || [])
    setLoading(false)
  }, [allowed])

  useEffect(() => {
    document.title = 'Auditoria · ISEN EDUCAE'
    load()
  }, [load])

  const logsSorted = useMemo(() => {
    const accessors: Record<string, (l: LogRow) => string | number> = {
      data: (l) => l.criado_em,
      usuario: (l) => l.usuario?.nome || '',
      acao: (l) => l.acao,
      tabela: (l) => l.tabela,
      registro: (l) => l.registro_id || '',
      antes: (l) => (l.dados_anteriores ? JSON.stringify(l.dados_anteriores) : ''),
      depois: (l) => (l.dados_novos ? JSON.stringify(l.dados_novos) : ''),
    }
    return tableSort.sortRows(logs, accessors)
  }, [logs, tableSort])

  if (!allowed) {
    return <div className="alert alert-error">Acesso restrito a admin e fiscal.</div>
  }

  if (loading) return <div className="loading">Carregando auditoria…</div>

  return (
    <div>
      <div className="page-header">
        <h1>Log de auditoria</h1>
        <button type="button" className="btn btn-secondary" onClick={load}>
          Atualizar
        </button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh label="Data" sortKey="data" activeKey={tableSort.sortKey} direction={tableSort.sortDir} onSort={tableSort.toggleSort} col="date" />
              <SortableTh label="Usuário" sortKey="usuario" activeKey={tableSort.sortKey} direction={tableSort.sortDir} onSort={tableSort.toggleSort} col="text" />
              <SortableTh label="Ação" sortKey="acao" activeKey={tableSort.sortKey} direction={tableSort.sortDir} onSort={tableSort.toggleSort} col="compact" />
              <SortableTh label="Tabela" sortKey="tabela" activeKey={tableSort.sortKey} direction={tableSort.sortDir} onSort={tableSort.toggleSort} col="compact" />
              <SortableTh label="Registro" sortKey="registro" activeKey={tableSort.sortKey} direction={tableSort.sortDir} onSort={tableSort.toggleSort} col="text" />
              <SortableTh label="Antes" sortKey="antes" activeKey={tableSort.sortKey} direction={tableSort.sortDir} onSort={tableSort.toggleSort} col="text" />
              <SortableTh label="Depois" sortKey="depois" activeKey={tableSort.sortKey} direction={tableSort.sortDir} onSort={tableSort.toggleSort} col="text" />
            </tr>
          </thead>
          <tbody>
            {logsSorted.map((l) => (
              <tr key={l.id}>
                <td className="col-date">{formatDate(l.criado_em)} {l.criado_em.slice(11, 19)}</td>
                <td className="col-text">{l.usuario?.nome || '—'}</td>
                <td className="col-compact">{l.acao}</td>
                <td className="col-compact">{l.tabela}</td>
                <td className="col-text">{l.registro_id}</td>
                <td className="col-text">
                  <code>
                    {l.dados_anteriores ? JSON.stringify(l.dados_anteriores).slice(0, 80) : '—'}
                  </code>
                </td>
                <td className="col-text">
                  <code>
                    {l.dados_novos ? JSON.stringify(l.dados_novos).slice(0, 80) : '—'}
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
